import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Mountain, MapPin, Phone, Globe, Clock, ArrowLeft, Share2,
  Facebook, Instagram, MessageCircle, Copy, Check, ChevronLeft,
  ChevronRight, ImageIcon, Navigation, Calendar, DollarSign,
  Car, Play, Info, Ruler, ExternalLink, ArrowRight, Eye, Compass
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import { AdBanner } from "@/components/shared/AdPlaceholder";
import axios from "axios";
import { API } from "@/App";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (url.startsWith("/uploads/")) return `${backendUrl}/api${url}`;
  return `${backendUrl}${url}`;
};

const getFacebookEmbedUrl = (url) => {
  if (!url) return null;
  return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=0&width=350`;
};

function parseDistances(distancesText) {
  if (!distancesText) return [];
  return distancesText.split('\n').filter(Boolean).map(line => {
    const match = line.match(/(?:desde\s+)?(.+?):\s*(.+)/i);
    if (match) return { city: match[1].replace(/^desde\s+/i, '').trim(), time: match[2].trim() };
    return { city: line.trim(), time: '' };
  }).filter(d => d.city);
}

export default function DestinoDetailPage() {
  const { slug } = useParams();
  const [destination, setDestination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    fetchDestination();
  }, [slug]);

  const fetchDestination = async () => {
    try {
      const response = await axios.get(`${API}/public/destino/${slug}`);
      setDestination(response.data);
      if (response.data?.destination_id) {
        axios.post(`${API}/track-view/destination/${response.data.destination_id}`).catch(() => {});
      }
    } catch (error) {
      console.error("Error fetching destination:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success("Enlace copiado al portapapeles");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = (platform) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`Descubre ${destination?.name} - Choluteca.net`);
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
    };
    window.open(shareUrls[platform], "_blank", "width=600,height=400");
  };

  const getAllImages = () => {
    const images = [];
    if (destination?.featured_image_url) {
      images.push({ url: destination.featured_image_url, isFeatured: true });
    }
    if (destination?.gallery_urls?.length > 0) {
      destination.gallery_urls.forEach((img) => {
        images.push({ url: typeof img === "string" ? img : img.url, isFeatured: false });
      });
    }
    return images;
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-[3px] border-emerald-600 border-t-transparent" />
            <p className="text-sm text-gray-400">Cargando destino...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!destination) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-4">
          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <Mountain className="h-10 w-10 text-gray-300" />
          </div>
          <h1 className="text-2xl font-bold text-gray-700 mb-2">Destino no encontrado</h1>
          <p className="text-gray-500 mb-6">El destino que buscas no existe o ha sido removido.</p>
          <Link to="/destinos">
            <Button className="rounded-xl">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver a Destinos
            </Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const allImages = getAllImages();
  const distances = parseDistances(destination.distances);
  const hasLocation = destination.lat && destination.lng;
  const mapUrl = hasLocation
    ? `https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${destination.lat},${destination.lng}&zoom=13&maptype=satellite`
    : null;

  return (
    <Layout>
      {/* === HERO with full-width image === */}
      <section className="relative" data-testid="destino-detail-hero">
        {/* Full-width image background */}
        <div className="relative h-[45vh] sm:h-[55vh] lg:h-[60vh] overflow-hidden">
          {allImages.length > 0 ? (
            <>
              {allImages.length === 1 ? (
                <img
                  src={getImageUrl(allImages[0].url)}
                  alt={destination.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Carousel className="w-full h-full" opts={{ loop: true }}>
                  <CarouselContent className="h-full -ml-0">
                    {allImages.map((img, index) => (
                      <CarouselItem key={index} className="h-full pl-0">
                        <div
                          className="h-[45vh] sm:h-[55vh] lg:h-[60vh] cursor-pointer"
                          onClick={() => { setLightboxIndex(index); setLightboxOpen(true); }}
                        >
                          <img
                            src={getImageUrl(img.url)}
                            alt={`${destination.name} - ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious className="left-4 bg-white/90 hover:bg-white shadow-lg h-10 w-10" />
                  <CarouselNext className="right-4 bg-white/90 hover:bg-white shadow-lg h-10 w-10" />
                </Carousel>
              )}
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-emerald-800 to-teal-900 flex items-center justify-center">
              <Mountain className="h-24 w-24 text-white/20" />
            </div>
          )}
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/40 to-transparent pointer-events-none" />

          {/* Top nav bar */}
          <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-10">
            <Link to="/destinos">
              <Button variant="ghost" className="bg-black/30 hover:bg-black/50 text-white backdrop-blur-md rounded-xl h-10 px-4">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Destinos
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              {allImages.length > 1 && (
                <span className="bg-black/30 backdrop-blur-md text-white text-xs px-3 py-2 rounded-xl flex items-center gap-1.5">
                  <ImageIcon className="h-3.5 w-3.5" />
                  {allImages.length} fotos
                </span>
              )}
              <Button
                onClick={() => setShareDialogOpen(true)}
                variant="ghost"
                className="bg-black/30 hover:bg-black/50 text-white backdrop-blur-md rounded-xl h-10 px-4"
                data-testid="share-button"
              >
                <Share2 className="h-4 w-4 mr-2" />
                Compartir
              </Button>
            </div>
          </div>

          {/* Title overlay at bottom */}
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 z-10">
            <div className="max-w-7xl mx-auto">
              <Badge className="bg-white/15 text-white border border-white/20 backdrop-blur-sm mb-3">
                {destination.category_name}
              </Badge>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-2 drop-shadow-lg">
                {destination.name}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
                {destination.municipio && (
                  <span className="flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-emerald-400" />
                    {destination.municipio}{destination.departamento ? `, ${destination.departamento}` : ''}
                  </span>
                )}
                {destination.view_count > 0 && (
                  <span className="flex items-center gap-1.5">
                    <Eye className="h-4 w-4" />
                    {destination.view_count} visitas
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === DISTANCES & LOCATION BAR === */}
      {(distances.length > 0 || hasLocation) && (
        <section className="bg-gray-950 border-t border-white/5" data-testid="destino-distances-bar">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              {distances.length > 0 && (
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-3 md:mb-0 md:mr-4">
                    <Car className="h-4 w-4 text-amber-400" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">Distancias</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {distances.map((d, i) => (
                      <motion.span
                        key={i}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm"
                      >
                        <Navigation className="h-3.5 w-3.5 text-amber-400" />
                        <span className="text-gray-400 text-sm">{d.city}</span>
                        <span className="font-bold text-white text-sm">{d.time}</span>
                      </motion.span>
                    ))}
                  </div>
                </div>
              )}
              {hasLocation && (
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${destination.lat},${destination.lng}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0"
                >
                  <Button className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl h-11 px-6" data-testid="directions-btn">
                    <Navigation className="h-4 w-4 mr-2" />
                    C&oacute;mo llegar
                    <ExternalLink className="h-3.5 w-3.5 ml-2 opacity-60" />
                  </Button>
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* === MAIN CONTENT === */}
      <section className="py-10 md:py-14 bg-gray-50" data-testid="destino-detail-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column */}
            <div className="lg:col-span-2 space-y-6">
              {/* Quick Info Pills */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex flex-wrap gap-3"
              >
                {destination.best_time_to_visit && (
                  <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl shadow-sm border border-gray-100">
                    <Calendar className="h-4 w-4 text-amber-600" />
                    <span className="text-sm">
                      <span className="text-gray-400">Mejor &eacute;poca:</span>{' '}
                      <span className="font-semibold text-gray-800">{destination.best_time_to_visit}</span>
                    </span>
                  </div>
                )}
                {destination.entry_fee && (
                  <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-white rounded-xl shadow-sm border border-gray-100">
                    <DollarSign className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm">
                      <span className="text-gray-400">Entrada:</span>{' '}
                      <span className="font-semibold text-gray-800">{destination.entry_fee}</span>
                    </span>
                  </div>
                )}
                {destination.whatsapp && (
                  <a
                    href={`https://wa.me/${destination.whatsapp.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-50 text-green-700 rounded-xl border border-green-100 hover:bg-green-100 transition-colors"
                  >
                    <MessageCircle className="h-4 w-4" />
                    <span className="text-sm font-semibold">WhatsApp</span>
                  </a>
                )}
              </motion.div>

              {/* Description */}
              {destination.description && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100"
                >
                  <h2 className="font-heading text-xl font-bold text-gray-900 mb-4">
                    Sobre este lugar
                  </h2>
                  <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                    {destination.description}
                  </p>
                </motion.div>
              )}

              {/* How to get there */}
              {destination.how_to_get_there && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-amber-50 flex items-center justify-center">
                      <Car className="h-4.5 w-4.5 text-amber-600" />
                    </div>
                    <h2 className="font-heading text-xl font-bold text-gray-900">C&oacute;mo llegar</h2>
                  </div>
                  <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                    {destination.how_to_get_there}
                  </p>
                </motion.div>
              )}

              {/* === MAP SECTION (inline) === */}
              {hasLocation && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100"
                  data-testid="destino-map"
                >
                  <div className="p-6 md:p-8 pb-4">
                    <div className="flex items-center gap-3 mb-1">
                      <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                        <MapPin className="h-4.5 w-4.5 text-emerald-600" />
                      </div>
                      <h2 className="font-heading text-xl font-bold text-gray-900">Ubicaci&oacute;n</h2>
                    </div>
                    {destination.address && (
                      <p className="text-gray-500 text-sm ml-12">{destination.address}</p>
                    )}
                  </div>
                  <div className="aspect-[16/7] md:aspect-[16/6]">
                    <iframe
                      title={`Mapa de ${destination.name}`}
                      src={`https://maps.google.com/maps?q=${destination.lat},${destination.lng}&z=14&output=embed`}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                  <div className="p-4 flex justify-end">
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${destination.lat},${destination.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="outline" className="rounded-xl text-sm" data-testid="map-directions-link">
                        <Navigation className="h-3.5 w-3.5 mr-1.5" />
                        Abrir en Google Maps
                        <ExternalLink className="h-3 w-3 ml-1.5 opacity-50" />
                      </Button>
                    </a>
                  </div>
                </motion.div>
              )}

              {/* Photo Gallery Grid */}
              {allImages.length > 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100"
                >
                  <h2 className="font-heading text-xl font-bold text-gray-900 mb-4">
                    Galer&iacute;a de Fotos
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {allImages.map((img, index) => (
                      <div
                        key={index}
                        className="aspect-square rounded-xl overflow-hidden cursor-pointer group relative"
                        onClick={() => { setLightboxIndex(index); setLightboxOpen(true); }}
                      >
                        <img
                          src={getImageUrl(img.url)}
                          alt={`${destination.name} - ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Facebook Videos */}
              {destination.facebook_videos?.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center">
                      <Play className="h-4.5 w-4.5 text-blue-600" />
                    </div>
                    <h2 className="font-heading text-xl font-bold text-gray-900">Videos</h2>
                  </div>
                  {destination.facebook_videos.length === 1 ? (
                    <div className="flex justify-center">
                      <div className="w-[350px] h-[620px] rounded-xl overflow-hidden bg-gray-100">
                        <iframe
                          src={getFacebookEmbedUrl(destination.facebook_videos[0])}
                          width="100%"
                          height="100%"
                          style={{ border: 'none', overflow: 'hidden' }}
                          scrolling="no"
                          frameBorder="0"
                          allowFullScreen
                          allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                        />
                      </div>
                    </div>
                  ) : (
                    <Carousel className="w-full" opts={{ loop: true, align: "center" }}>
                      <CarouselContent className="-ml-3">
                        {destination.facebook_videos.map((videoUrl, index) => (
                          <CarouselItem key={index} className="pl-3 basis-full sm:basis-1/2 lg:basis-1/3">
                            <div className="w-full max-w-[350px] mx-auto h-[620px] rounded-xl overflow-hidden bg-gray-100">
                              <iframe
                                src={getFacebookEmbedUrl(videoUrl)}
                                width="100%"
                                height="100%"
                                style={{ border: 'none', overflow: 'hidden' }}
                                scrolling="no"
                                frameBorder="0"
                                allowFullScreen
                                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                              />
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <CarouselPrevious className="left-0 bg-white/90 hover:bg-white shadow-lg" />
                      <CarouselNext className="right-0 bg-white/90 hover:bg-white shadow-lg" />
                    </Carousel>
                  )}
                </motion.div>
              )}

              {/* Additional Info */}
              {destination.additional_info && (
                <motion.div
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-xl bg-violet-50 flex items-center justify-center">
                      <Info className="h-4.5 w-4.5 text-violet-600" />
                    </div>
                    <h2 className="font-heading text-xl font-bold text-gray-900">Informaci&oacute;n Adicional</h2>
                  </div>
                  <p className="text-gray-600 whitespace-pre-line leading-relaxed">
                    {destination.additional_info}
                  </p>
                </motion.div>
              )}

              <AdBanner />
            </div>

            {/* === RIGHT SIDEBAR === */}
            <div className="space-y-6">
              {/* Quick info card */}
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 sticky top-24"
                data-testid="destino-info-card"
              >
                <h3 className="font-heading text-lg font-bold text-gray-900 mb-5">Informaci&oacute;n</h3>

                <div className="space-y-4">
                  {destination.best_time_to_visit && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center shrink-0">
                        <Calendar className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Mejor &eacute;poca</p>
                        <p className="font-semibold text-gray-800 text-sm">{destination.best_time_to_visit}</p>
                      </div>
                    </div>
                  )}

                  {destination.entry_fee && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
                        <DollarSign className="h-5 w-5 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Entrada</p>
                        <p className="font-semibold text-gray-800 text-sm">{destination.entry_fee}</p>
                      </div>
                    </div>
                  )}

                  {destination.address && (
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                        <MapPin className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Ubicaci&oacute;n</p>
                        <p className="font-semibold text-gray-800 text-sm">{destination.address}</p>
                      </div>
                    </div>
                  )}

                  {destination.phone && (
                    <a href={`tel:${destination.phone}`} className="flex items-start gap-3 group">
                      <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center shrink-0">
                        <Phone className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Tel&eacute;fono</p>
                        <p className="font-semibold text-purple-600 text-sm group-hover:underline">{destination.phone}</p>
                      </div>
                    </a>
                  )}

                  {destination.website && (
                    <a href={destination.website} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 group">
                      <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center shrink-0">
                        <Globe className="h-5 w-5 text-gray-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase tracking-wider">Sitio web</p>
                        <p className="font-semibold text-gray-700 text-sm group-hover:underline">Visitar sitio</p>
                      </div>
                    </a>
                  )}
                </div>

                {/* Social links */}
                {(destination.facebook || destination.instagram) && (
                  <div className="mt-6 pt-5 border-t border-gray-100">
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-3">Redes Sociales</p>
                    <div className="flex gap-2">
                      {destination.facebook && (
                        <a
                          href={destination.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
                        >
                          <Facebook className="h-4.5 w-4.5" />
                        </a>
                      )}
                      {destination.instagram && (
                        <a
                          href={destination.instagram}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-pink-500 flex items-center justify-center text-white hover:opacity-90 transition-opacity"
                        >
                          <Instagram className="h-4.5 w-4.5" />
                        </a>
                      )}
                    </div>
                  </div>
                )}

                {/* Tags */}
                {destination.tags?.length > 0 && (
                  <div className="mt-5 pt-5 border-t border-gray-100">
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wider mb-3">Tags</p>
                    <div className="flex flex-wrap gap-2">
                      {destination.tags.map((tag, i) => (
                        <span key={i} className="px-3 py-1 bg-gray-50 text-gray-500 text-xs rounded-lg border border-gray-100 font-medium">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Share */}
                <Button
                  onClick={() => setShareDialogOpen(true)}
                  variant="outline"
                  className="w-full mt-6 rounded-xl"
                  data-testid="sidebar-share-btn"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Compartir este destino
                </Button>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* === CTA === */}
      <section className="relative py-20 overflow-hidden bg-gray-950">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff'%3E%3Ccircle cx='20' cy='20' r='1.5'/%3E%3C/g%3E%3C/svg%3E")`,
        }} />
        <div className="absolute top-0 left-0 w-[400px] h-[300px] bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Explora m&aacute;s destinos
            </h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              Descubre todos los lugares incre&iacute;bles que Choluteca tiene para ofrecer
            </p>
            <Link to="/destinos">
              <Button
                size="lg"
                className="bg-emerald-600 hover:bg-emerald-500 text-white text-lg px-8 py-6 rounded-2xl shadow-lg shadow-emerald-900/30"
              >
                Ver todos los destinos
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Compartir {destination?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={typeof window !== "undefined" ? window.location.href : ""}
                className="flex-1 px-3 py-2 border rounded-xl text-sm bg-gray-50"
              />
              <Button onClick={handleCopyLink} variant="outline" className="rounded-xl">
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <div className="flex justify-center gap-3">
              <button
                onClick={() => handleShare("facebook")}
                className="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
                data-testid="share-facebook"
              >
                <Facebook className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleShare("whatsapp")}
                className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center text-white hover:bg-green-600 transition-colors"
                data-testid="share-whatsapp"
              >
                <MessageCircle className="h-5 w-5" />
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-5xl bg-black/95 border-0 p-2">
          <div className="relative">
            <img
              src={getImageUrl(allImages[lightboxIndex]?.url)}
              alt={`${destination?.name} - ${lightboxIndex + 1}`}
              className="w-full max-h-[80vh] object-contain"
            />
            {allImages.length > 1 && (
              <>
                <button
                  onClick={() => setLightboxIndex((lightboxIndex - 1 + allImages.length) % allImages.length)}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={() => setLightboxIndex((lightboxIndex + 1) % allImages.length)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white text-sm px-4 py-1.5 rounded-full backdrop-blur-sm">
                  {lightboxIndex + 1} / {allImages.length}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
